import { useEffect, useState } from "react";
import type React from "react";
import { Center, Loader, Text, Stack } from "@mantine/core";
import { useAuth } from "tecrmi_components/useAuth";
import { setGlobalAccessTokenFn } from "@utils/httpClient";
import { setAccessToken, clearAccessToken, setAccessTokenRefresher } from "@utils/tokenStore";

interface ContextProviderProps {
  children: React.ReactNode;
}

/**
 * Bridges the BeeHive `useAuth()` context into:
 *   1. The shared HttpClient via `setGlobalAccessTokenFn` (for REST calls).
 *   2. The in-memory token store (for the WebSocket client, which cannot
 *      easily call an async accessor on every reconnect). The token is
 *      deliberately NOT persisted: a bearer token in localStorage is
 *      readable by any script on the page and outlives the tab.
 *
 * Renders a brief loading state while the initial token is fetched so that
 * the first dashboard request never goes out without an Authorization header.
 * Pattern adapted from the extraction frontend's ApplicationProvider.
 */
export const ContextProvider: React.FC<ContextProviderProps> = ({
  children,
}) => {
  const { getValidAccessToken, currentSession, loading } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    setGlobalAccessTokenFn(getValidAccessToken);
    setAccessTokenRefresher(getValidAccessToken);
  }, [getValidAccessToken]);

  useEffect(() => {
    let cancelled = false;
    const storeToken = async (): Promise<void> => {
      const token = await getValidAccessToken();
      if (cancelled) return;
      if (token) {
        setAccessToken(token);
        setTokenReady(true);
      } else {
        clearAccessToken();
        setTokenReady(false);
      }
    };
    if (currentSession) {
      void storeToken();
    } else {
      clearAccessToken();
      setTokenReady(false);
    }
    return () => {
      cancelled = true;
    };
  }, [currentSession, getValidAccessToken]);

  if (loading || (currentSession && !tokenReady)) {
    return (
      <Center mih="60vh" aria-busy="true" aria-live="polite">
        <Stack align="center" gap="xs">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Checking authentication...
          </Text>
        </Stack>
      </Center>
    );
  }

  return <>{children}</>;
};

export default ContextProvider;
